import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const NevadaSmogEmissionsGuidePage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />
    <section className="pt-28 pb-12 bg-obsidian-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/nevada-dmv-info" className="hover:text-gold-400">DMV Info</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Smog & Emissions Testing</span>
        </div>
        <h1 className="font-display text-4xl font-bold text-white mb-3">Nevada Smog & Emissions Testing Guide</h1>
        <p className="text-obsidian-300">Everything you need to know about Nevada emissions testing requirements, exemptions, and waivers.</p>
      </div>
    </section>
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <EducationalDisclaimer />
      <div className="space-y-8 text-obsidian-700">
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Who Needs an Emissions Test?</h2>
          <p>Smog checks are required annually for registration renewal of gasoline and diesel vehicles (up to 14,000 lbs) based in the urban areas of <strong>Clark County (Las Vegas)</strong> or <strong>Washoe County (Reno/Sparks)</strong>.</p>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Emissions Exemptions</h2>
          <div className="bg-green-50 border border-green-200 rounded-xl p-5">
            <p className="font-semibold text-green-800 text-sm mb-3">The following vehicles are exempt from emissions testing:</p>
            <ul className="space-y-2">
              {[
                'New vehicles on their first three registrations',
                'New hybrid vehicles for their first five model years',
                'Electric vehicles (fully electric)',
                'Motorcycles and mopeds',
                '1967 or older model year vehicles',
                '"Classic" vehicles driven less than 5,000 miles per year',
                'Diesel vehicles 1967 or older',
              ].map(item => (
                <li key={item} className="flex items-start space-x-2 text-sm text-green-700">
                  <i className="ri-checkbox-circle-line flex-shrink-0 mt-0.5"></i>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Test Validity</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { title: 'Standard Validity', desc: 'Emissions tests are valid for 90 days from the test date.' },
              { title: 'Dealer Sale Validity', desc: 'Tests from a Nevada dealer sale are valid for 180 days.' },
              { title: 'Clark County', desc: 'Must use an authorized emissions testing station in Clark County.' },
              { title: 'Washoe County', desc: 'Must use an authorized emissions testing station in Washoe County.' },
            ].map(item => (
              <div key={item.title} className="border border-obsidian-100 rounded-lg p-4">
                <p className="font-semibold text-obsidian-900 text-sm mb-1">{item.title}</p>
                <p className="text-obsidian-500 text-xs">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>
        <section>
          <h2 className="font-display text-2xl font-bold text-obsidian-900 mb-4">Emissions Waivers</h2>
          <p className="text-sm">If your vehicle fails the emissions test, you may be eligible for a waiver after making qualifying repairs. Minimum spending requirements apply:</p>
          <div className="mt-4 bg-obsidian-50 rounded-xl p-6">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-obsidian-200"><th className="text-left py-2 font-semibold text-obsidian-900">County</th><th className="text-right py-2 font-semibold text-obsidian-900">Minimum Repair Spending</th></tr></thead>
              <tbody className="divide-y divide-obsidian-100">
                {[['Clark County (Las Vegas)', '$450 at an authorized 2G station'], ['Washoe County (Reno/Sparks)', '$200 at an authorized 2G station']].map(([l, a]) => (
                  <tr key={l}><td className="py-2.5 text-obsidian-700">{l}</td><td className="py-2.5 text-right font-medium text-obsidian-900">{a}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <div className="bg-gold-50 border border-gold-200 rounded-xl p-6 text-center">
          <h3 className="font-display font-bold text-obsidian-900 text-lg mb-2">Need Help With Emissions Requirements?</h3>
          <p className="text-obsidian-600 text-sm mb-4">Speedy DMV Services can guide you through the emissions process and handle your registration once you pass.</p>
          <Link to="/appointment" className="inline-block bg-gold-gradient text-obsidian-900 px-6 py-2.5 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

export default NevadaSmogEmissionsGuidePage;
